import { useState, useEffect, useMemo } from 'react';
import { ReportHeader } from '@/components/reports/ReportUI';
import { BranchMultiSelect } from '@/components/BranchMultiSelect';
import { ExportTrio } from '@/components/accounting/ExportTrio';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { FileText, TrendingUp, TrendingDown, Scale, Printer, Building2, CalendarDays, FileCheck, CalendarClock } from 'lucide-react';
import { useBranchStore } from '@/stores';
import { cur } from '@/lib/currency';
import api from '@/lib/api';
import { toast } from 'sonner';
import VATReturnDraftDialog from '@/components/VATReturnDraftDialog';

// iter895 — Monthly VAT Summary Report for tax filings.
// Filters: month (YYYY-MM) + branch. Shows Output VAT (sales) + Input
// VAT (bills + expenses) breakdown, computes Net VAT Due/Refundable,
// exports the whole thing as a printable PDF via the shared print
// pipeline so it also works on Android tablets with a thermal printer.
export default function VATSummaryPage() {
  const { branches, fetchBranches } = useBranchStore();
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [branchSel, setBranchSel] = useState([]);
  const branchId = branchSel.length === 1 ? branchSel[0] : 'all';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [draftReturnOpen, setDraftReturnOpen] = useState(false);

  useEffect(() => { fetchBranches(); }, [fetchBranches]);

  const fetchSummary = async () => {
    setLoading(true);
    try {
      const params = { month };
      if (branchSel.length) params.branch_ids = branchSel.join(',');
      const { data: res } = await api.get('/accounting/vat-summary', { params });
      setData(res);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load VAT summary');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchSummary(); /* eslint-disable-next-line */ }, [month, branchSel]);

  const monthLabel = useMemo(() => {
    if (!month) return '';
    const [y, m] = month.split('-');
    return new Date(+y, +m - 1, 1).toLocaleString('en-US', { month: 'long', year: 'numeric' });
  }, [month]);

  // iter896 — Estimated tax due date banner. Filing frequency is
  // pulled from the tenant/branch tax config (monthly by default). We
  // compute the last day of the CURRENT return period + 30 days
  // (the KSA + most Middle East conventions grace period). If the
  // report month is in the past AND today is past the due date, the
  // banner turns rose to flag an OVERDUE filing.
  const dueBanner = useMemo(() => {
    if (!month) return null;
    const [y, m] = month.split('-').map(Number);
    // First of the next month → subtract 1 day = last day of the return period.
    const returnEnd = new Date(y, m, 0);
    // Default filing frequency: monthly with 30-day grace window. We
    // don't have per-tenant frequency yet, so this covers the KSA
    // (monthly, due end of month +1) and generic monthly cadence.
    const dueDate = new Date(returnEnd);
    dueDate.setDate(dueDate.getDate() + 30);
    const today = new Date();
    const daysUntilDue = Math.round((dueDate - today) / (24 * 60 * 60 * 1000));
    const overdue = daysUntilDue < 0;
    return {
      dueDate: dueDate.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }),
      daysUntilDue,
      overdue,
    };
  }, [month]);

  const netVat = data?.summary?.net_vat_due ?? 0;
  const status = data?.summary?.status || 'ZERO';
  const isPayable = status === 'PAYABLE';
  const isRefundable = status === 'REFUNDABLE';

  const handleExportPdf = async () => {
    if (!data) return;
    const htmlFallback = `<html><head><title>VAT Summary ${monthLabel}</title>
<style>
  body{font-family:'Helvetica Neue',Arial,sans-serif;padding:32px;color:#111;font-size:12px}
  h1{margin:0 0 4px;font-size:22px;color:#0b3;}
  .sub{color:#666;font-size:11px;margin-bottom:16px}
  .grid{display:grid;grid-template-columns:repeat(3,1fr);gap:12px;margin:12px 0 20px}
  .card{border:1px solid #ddd;border-radius:8px;padding:12px}
  .card .lbl{font-size:10px;text-transform:uppercase;color:#666;letter-spacing:.06em}
  .card .val{font-size:20px;font-weight:700;margin-top:4px}
  .payable{color:#b91c1c}.refundable{color:#047857}.zero{color:#374151}
  table{width:100%;border-collapse:collapse;margin:8px 0 18px;font-size:11px}
  th,td{border-bottom:1px solid #eee;padding:6px 8px;text-align:left}
  th{background:#f8f8f8;font-weight:600;text-transform:uppercase;letter-spacing:.05em;font-size:9px;color:#555}
  td.num,th.num{text-align:right;font-variant-numeric:tabular-nums}
  tr.total td{font-weight:700;border-top:2px solid #333;background:#f8f8f8}
  .section-title{font-size:14px;font-weight:700;margin:16px 0 4px;padding-bottom:4px;border-bottom:2px solid #0b3}
  .footer{margin-top:22px;padding-top:8px;border-top:1px solid #eee;font-size:9px;color:#999;text-align:center}
</style></head><body>
<h1>Monthly VAT Summary</h1>
<div class="sub">${monthLabel} · Branch: ${data.branch_name || 'All Branches'} · Generated ${new Date().toLocaleString()}</div>
<div class="grid">
  <div class="card"><div class="lbl">Output VAT (Sales)</div><div class="val">${cur()} ${(data.summary.output_vat||0).toLocaleString()}</div></div>
  <div class="card"><div class="lbl">Input VAT (Purchases)</div><div class="val">${cur()} ${(data.summary.input_vat||0).toLocaleString()}</div></div>
  <div class="card"><div class="lbl">Net VAT ${status}</div><div class="val ${status.toLowerCase()}">${cur()} ${Math.abs(data.summary.net_vat_due||0).toLocaleString()}</div></div>
</div>
<div class="section-title">Output VAT — Sales (${data.output_vat.totals.count})</div>
<table><thead><tr><th>Date</th><th>Invoice</th><th>Customer</th><th>Branch</th><th class="num">Net</th><th class="num">VAT %</th><th class="num">VAT</th><th class="num">Total</th></tr></thead>
<tbody>
${(data.output_vat.rows||[]).map(r=>`<tr><td>${r.date}</td><td>${r.invoice}</td><td>${r.party}</td><td>${r.branch}</td><td class="num">${r.net_amount.toLocaleString()}</td><td class="num">${r.vat_rate}%</td><td class="num">${r.vat_amount.toLocaleString()}</td><td class="num">${r.total.toLocaleString()}</td></tr>`).join('')}
<tr class="total"><td colspan="4">TOTAL</td><td class="num">${data.output_vat.totals.net.toLocaleString()}</td><td></td><td class="num">${data.output_vat.totals.vat.toLocaleString()}</td><td class="num">${data.output_vat.totals.gross.toLocaleString()}</td></tr>
</tbody></table>
<div class="section-title">Input VAT — Purchases + Expenses (${data.input_vat.totals.count})</div>
<table><thead><tr><th>Date</th><th>Ref</th><th>Supplier / Vendor</th><th>Branch</th><th class="num">Net</th><th class="num">VAT %</th><th class="num">VAT</th><th class="num">Total</th></tr></thead>
<tbody>
${(data.input_vat.rows||[]).map(r=>`<tr><td>${r.date}</td><td>${r.invoice}</td><td>${r.party}</td><td>${r.branch}</td><td class="num">${r.net_amount.toLocaleString()}</td><td class="num">${r.vat_rate}%</td><td class="num">${r.vat_amount.toLocaleString()}</td><td class="num">${r.total.toLocaleString()}</td></tr>`).join('')}
<tr class="total"><td colspan="4">TOTAL</td><td class="num">${data.input_vat.totals.net.toLocaleString()}</td><td></td><td class="num">${data.input_vat.totals.vat.toLocaleString()}</td><td class="num">${data.input_vat.totals.gross.toLocaleString()}</td></tr>
</tbody></table>
<div class="footer">Powered by SMARTO ERP · This is a computed VAT summary, not an official tax return.</div>
<script>window.onload=()=>{setTimeout(()=>window.print(),150)}</script>
</body></html>`;

    const { printSummary } = await import('@/lib/printSummary');
    let printers = [];
    try { const { data: p } = await api.get('/printers'); printers = Array.isArray(p) ? p : (p?.items || []); } catch { /* fallback path handles no printers */ }
    await printSummary({
      printers,
      branchId: branchId !== 'all' ? branchId : null,
      title: `VAT Summary ${monthLabel}`,
      htmlFallback,
    });
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="vat-summary-page">
        <ReportHeader icon={Scale} title="Monthly VAT Summary" subtitle={`Output vs Input VAT for tax filings · ${monthLabel}`} testId="vat-summary-header"
          actions={<Button onClick={() => setDraftReturnOpen(true)} disabled={!data || loading} size="sm" className="rounded-xl gap-1.5 bg-emerald-600 hover:bg-emerald-700" data-testid="vat-summary-draft-return-btn">
            <FileCheck size={14} /> Draft Return
          </Button>} />
        <div className="flex flex-wrap items-center gap-2" data-testid="vat-summary-filter">
          <label className="flex items-center gap-1.5 text-xs text-muted-foreground"><CalendarDays size={14} />Month
            <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="h-8 w-[9.5rem] text-xs" data-testid="vat-summary-month" />
          </label>
          <BranchMultiSelect value={branchSel} onChange={setBranchSel} branches={branches} testid="vat-summary-branch" />
          <Button onClick={handleExportPdf} disabled={!data || loading} variant="outline" size="sm" className="rounded-xl gap-1.5 h-8 text-xs" data-testid="vat-summary-export-btn">
            <Printer size={14} /> Print / PDF
          </Button>
          <ExportTrio disabled={!data} testId="vat-summary-export" spec={() => ({
            title: `VAT Summary ${monthLabel}`, subtitle: data?.branch_name || 'All Branches', filename: `vat_summary_${month}`,
            cols: [{ key: 'side', label: 'Side' }, { key: 'date', label: 'Date' }, { key: 'invoice', label: 'Invoice' }, { key: 'party', label: 'Party' }, { key: 'branch', label: 'Branch' },
              { key: 'net_amount', label: 'Net', numeric: true }, { key: 'vat_rate', label: 'VAT %', numeric: true }, { key: 'vat_amount', label: 'VAT', numeric: true }, { key: 'total', label: 'Total', numeric: true }],
            rows: [...(data?.output_vat?.rows || []).map((r) => ({ ...r, side: 'Output' })), ...(data?.input_vat?.rows || []).map((r) => ({ ...r, side: 'Input' }))],
          })} />
        </div>

        {/* iter896 — Filing due-date banner. Auto-computed from month
            + 30-day grace window; turns rose when the deadline has
            passed. */}
        {dueBanner && (
          <div
            data-testid="vat-summary-due-banner"
            className={`rounded-xl px-4 py-2.5 text-sm flex flex-wrap items-center justify-between gap-2 border ${
              dueBanner.overdue
                ? 'bg-rose-50/70 dark:bg-rose-950/25 border-rose-300/60 dark:border-rose-800/50 text-rose-800 dark:text-rose-300'
                : dueBanner.daysUntilDue <= 7
                  ? 'bg-amber-50/70 dark:bg-amber-950/25 border-amber-300/60 dark:border-amber-800/50 text-amber-800 dark:text-amber-300'
                  : 'bg-sky-50/70 dark:bg-sky-950/25 border-sky-300/60 dark:border-sky-800/50 text-sky-800 dark:text-sky-300'
            }`}
          >
            <div className="flex items-center gap-2">
              <CalendarClock size={16} />
              <span>
                <b>Next return due:</b> {dueBanner.dueDate}
                {' · '}
                {dueBanner.overdue
                  ? <span className="font-semibold">{Math.abs(dueBanner.daysUntilDue)} day{Math.abs(dueBanner.daysUntilDue) === 1 ? '' : 's'} overdue</span>
                  : dueBanner.daysUntilDue === 0
                    ? <span className="font-semibold">Due today</span>
                    : <span>{dueBanner.daysUntilDue} day{dueBanner.daysUntilDue === 1 ? '' : 's'} remaining</span>}
                {data && ` · Est. ${cur()} ${Math.abs(data.summary?.net_vat_due || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${data.summary?.status === 'REFUNDABLE' ? 'refundable' : 'to file'}`}
              </span>
            </div>
            {!dueBanner.overdue && data && (
              <Button size="sm" variant="ghost" onClick={() => setDraftReturnOpen(true)} className="h-7 text-xs">
                Draft Return →
              </Button>
            )}
          </div>
        )}

        {/* Summary cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Card className="border-emerald-200 dark:border-emerald-900/50 bg-emerald-50/40 dark:bg-emerald-950/20">
            <CardContent className="pt-5">
              <div className="flex items-center justify-between">
                <p className="text-[10px] uppercase tracking-wider text-emerald-700 dark:text-emerald-400 font-semibold">Output VAT (Sales)</p>
                <TrendingUp size={16} className="text-emerald-600" />
              </div>
              <p className="text-2xl font-bold text-emerald-800 dark:text-emerald-300 mt-2" data-testid="vat-summary-output-total">
                {cur()} {(data?.summary?.output_vat ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </p>
              <p className="text-xs text-muted-foreground mt-1">{data?.output_vat?.totals?.count || 0} taxable sales</p>
            </CardContent>
          </Card>
          <Card className="border-amber-200 dark:border-amber-900/50 bg-amber-50/40 dark:bg-amber-950/20">
            <CardContent className="pt-5">
              <div className="flex items-center justify-between">
                <p className="text-[10px] uppercase tracking-wider text-amber-700 dark:text-amber-400 font-semibold">Input VAT (Purchases)</p>
                <TrendingDown size={16} className="text-amber-600" />
              </div>
              <p className="text-2xl font-bold text-amber-800 dark:text-amber-300 mt-2" data-testid="vat-summary-input-total">
                {cur()} {(data?.summary?.input_vat ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </p>
              <p className="text-xs text-muted-foreground mt-1">{data?.input_vat?.totals?.count || 0} taxable purchases + expenses</p>
            </CardContent>
          </Card>
          <Card className={
            isPayable ? 'border-rose-300 dark:border-rose-900/50 bg-rose-50/50 dark:bg-rose-950/20'
              : isRefundable ? 'border-sky-300 dark:border-sky-900/50 bg-sky-50/50 dark:bg-sky-950/20'
              : 'border-muted bg-muted/30'
          }>
            <CardContent className="pt-5">
              <div className="flex items-center justify-between">
                <p className={`text-[10px] uppercase tracking-wider font-semibold ${
                  isPayable ? 'text-rose-700 dark:text-rose-400' :
                  isRefundable ? 'text-sky-700 dark:text-sky-400' :
                  'text-muted-foreground'
                }`}>
                  Net VAT {status}
                </p>
                <Scale size={16} className={
                  isPayable ? 'text-rose-600' : isRefundable ? 'text-sky-600' : 'text-muted-foreground'
                } />
              </div>
              <p className={`text-2xl font-bold mt-2 ${
                isPayable ? 'text-rose-800 dark:text-rose-300' :
                isRefundable ? 'text-sky-800 dark:text-sky-300' :
                'text-foreground'
              }`} data-testid="vat-summary-net-total">
                {cur()} {Math.abs(netVat).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </p>
              <Badge variant="outline" className={`mt-1 text-[10px] ${
                isPayable ? 'border-rose-300 text-rose-700' :
                isRefundable ? 'border-sky-300 text-sky-700' :
                'border-muted-foreground/30'
              }`} data-testid="vat-summary-status-badge">
                {isPayable ? 'Pay to tax authority' : isRefundable ? 'Refundable' : 'Zero balance'}
              </Badge>
            </CardContent>
          </Card>
        </div>

        {/* Output VAT (Sales) table */}
        <Card>
          <CardContent className="p-0">
            <div className="flex items-center justify-between px-4 py-3 border-b bg-emerald-50/30 dark:bg-emerald-950/10">
              <div className="flex items-center gap-2">
                <TrendingUp size={16} className="text-emerald-600" />
                <h2 className="font-semibold">Output VAT — Sales</h2>
                <Badge variant="secondary">{data?.output_vat?.totals?.count || 0}</Badge>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/40 border-b text-xs uppercase tracking-wide">
                  <tr>
                    <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">Date</th>
                    <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">Invoice</th>
                    <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">Customer</th>
                    <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">Branch</th>
                    <th className="text-right px-4 py-2.5 font-medium text-muted-foreground">Net</th>
                    <th className="text-right px-4 py-2.5 font-medium text-muted-foreground">VAT %</th>
                    <th className="text-right px-4 py-2.5 font-medium text-emerald-700">VAT</th>
                    <th className="text-right px-4 py-2.5 font-medium text-muted-foreground">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={8} className="text-center py-10 text-muted-foreground">Loading…</td></tr>
                  ) : (data?.output_vat?.rows || []).length === 0 ? (
                    <tr><td colSpan={8} className="text-center py-10 text-muted-foreground">No taxable sales this month.</td></tr>
                  ) : data.output_vat.rows.map((r, i) => (
                    <tr key={i} className="border-b hover:bg-muted/30" data-testid={`vat-out-row-${i}`}>
                      <td className="px-4 py-2.5 text-muted-foreground text-xs tabular-nums">{r.date}</td>
                      <td className="px-4 py-2.5 font-mono text-xs">{r.invoice}</td>
                      <td className="px-4 py-2.5">{r.party}</td>
                      <td className="px-4 py-2.5 text-muted-foreground text-xs">{r.branch}</td>
                      <td className="px-4 py-2.5 text-right tabular-nums">{r.net_amount.toLocaleString()}</td>
                      <td className="px-4 py-2.5 text-right text-muted-foreground text-xs">{r.vat_rate}%</td>
                      <td className="px-4 py-2.5 text-right tabular-nums font-semibold text-emerald-700">{r.vat_amount.toLocaleString()}</td>
                      <td className="px-4 py-2.5 text-right tabular-nums">{r.total.toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
                {data?.output_vat?.rows?.length > 0 && (
                  <tfoot className="bg-emerald-50/70 dark:bg-emerald-950/30 border-t-2 border-emerald-500/40 font-semibold">
                    <tr>
                      <td colSpan={4} className="px-4 py-2.5 uppercase tracking-wide text-xs">Total</td>
                      <td className="px-4 py-2.5 text-right tabular-nums">{data.output_vat.totals.net.toLocaleString()}</td>
                      <td></td>
                      <td className="px-4 py-2.5 text-right tabular-nums text-emerald-800">{data.output_vat.totals.vat.toLocaleString()}</td>
                      <td className="px-4 py-2.5 text-right tabular-nums">{data.output_vat.totals.gross.toLocaleString()}</td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Input VAT (Purchases + Expenses) table */}
        <Card>
          <CardContent className="p-0">
            <div className="flex items-center justify-between px-4 py-3 border-b bg-amber-50/30 dark:bg-amber-950/10">
              <div className="flex items-center gap-2">
                <TrendingDown size={16} className="text-amber-600" />
                <h2 className="font-semibold">Input VAT — Purchases + Expenses</h2>
                <Badge variant="secondary">{data?.input_vat?.totals?.count || 0}</Badge>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/40 border-b text-xs uppercase tracking-wide">
                  <tr>
                    <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">Date</th>
                    <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">Ref</th>
                    <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">Supplier / Vendor</th>
                    <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">Branch</th>
                    <th className="text-right px-4 py-2.5 font-medium text-muted-foreground">Net</th>
                    <th className="text-right px-4 py-2.5 font-medium text-muted-foreground">VAT %</th>
                    <th className="text-right px-4 py-2.5 font-medium text-amber-700">VAT</th>
                    <th className="text-right px-4 py-2.5 font-medium text-muted-foreground">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={8} className="text-center py-10 text-muted-foreground">Loading…</td></tr>
                  ) : (data?.input_vat?.rows || []).length === 0 ? (
                    <tr><td colSpan={8} className="text-center py-10 text-muted-foreground">No taxable purchases or expenses this month.</td></tr>
                  ) : data.input_vat.rows.map((r, i) => (
                    <tr key={i} className="border-b hover:bg-muted/30" data-testid={`vat-in-row-${i}`}>
                      <td className="px-4 py-2.5 text-muted-foreground text-xs tabular-nums">{r.date}</td>
                      <td className="px-4 py-2.5 font-mono text-xs">{r.invoice}</td>
                      <td className="px-4 py-2.5">{r.party} {r.source === 'expense' && <Badge variant="outline" className="text-[9px] ml-1">exp</Badge>}</td>
                      <td className="px-4 py-2.5 text-muted-foreground text-xs">{r.branch}</td>
                      <td className="px-4 py-2.5 text-right tabular-nums">{r.net_amount.toLocaleString()}</td>
                      <td className="px-4 py-2.5 text-right text-muted-foreground text-xs">{r.vat_rate}%</td>
                      <td className="px-4 py-2.5 text-right tabular-nums font-semibold text-amber-700">{r.vat_amount.toLocaleString()}</td>
                      <td className="px-4 py-2.5 text-right tabular-nums">{r.total.toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
                {data?.input_vat?.rows?.length > 0 && (
                  <tfoot className="bg-amber-50/70 dark:bg-amber-950/30 border-t-2 border-amber-500/40 font-semibold">
                    <tr>
                      <td colSpan={4} className="px-4 py-2.5 uppercase tracking-wide text-xs">Total</td>
                      <td className="px-4 py-2.5 text-right tabular-nums">{data.input_vat.totals.net.toLocaleString()}</td>
                      <td></td>
                      <td className="px-4 py-2.5 text-right tabular-nums text-amber-800">{data.input_vat.totals.vat.toLocaleString()}</td>
                      <td className="px-4 py-2.5 text-right tabular-nums">{data.input_vat.totals.gross.toLocaleString()}</td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </CardContent>
        </Card>

        <p className="text-xs text-muted-foreground text-center pb-4">
          This is a computed VAT summary — always cross-check against your local tax authority filing before submission.
        </p>

        <VATReturnDraftDialog
          open={draftReturnOpen}
          onOpenChange={setDraftReturnOpen}
          month={month}
          branchId={branchId}
          monthLabel={monthLabel}
        />
      </div>
    </DashboardLayout>
  );
}
